// The Rust lane as a picture: venue → rsh boxes → wz0 (feedgw, relay, g1-engine) → clients, drawn from the
// live /syshealth snapshot. Every node is a button; the page shows the drilldown for whichever is selected.
// Edge colour is the path (market data / execution / account / control); a standby box's edge is dashed and
// the primary's is solid, so a failover shows up as the solid line moving.

import type { SysHealth } from '@/lib/rustLane/api';
import { fmt } from '@/lib/rustLane/api';

export type NodeId = 'kalshi' | `rsh:${string}` | 'feedgw' | 'relay' | 'engine' | 'browser' | 'operator';

const C = { data: '#4fa3ff', exec: '#ff9f43', acct: '#3ddc97', ctl: '#8b97a6' };

function Node({ id, x, y, w, h, title, lines, state, selected, onSelect }: {
  id: NodeId; x: number; y: number; w: number; h: number; title: string; lines: string[];
  state: 'ok' | 'warn' | 'down' | 'off'; selected: boolean; onSelect: (id: NodeId) => void;
}) {
  const dot = state === 'ok' ? '#3ddc97' : state === 'warn' ? '#ffd166' : state === 'down' ? '#ff5c5c' : '#5d6a7a';
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(id); } }}
      className="cursor-pointer focus:outline-none"
    >
      <rect x={x} y={y} width={w} height={h} rx={4} className={selected ? 'fill-muted stroke-foreground' : 'fill-card stroke-border hover:stroke-foreground'} strokeWidth={selected ? 1.6 : 1.1} />
      <circle cx={x + w - 10} cy={y + 11} r={4} fill={dot} />
      <text x={x + 9} y={y + 16} className="fill-foreground font-mono text-[11.5px] font-semibold">{title}</text>
      {lines.map((l, i) => (
        <text key={i} x={x + 9} y={y + 32 + i * 13} className="fill-muted-foreground font-mono text-[10.5px]">{l}</text>
      ))}
    </g>
  );
}

function Edge({ d, color, dashed, label, lx, ly, anchor }: { d: string; color: string; dashed?: boolean; label?: string; lx?: number; ly?: number; anchor?: 'start' | 'middle' | 'end' }) {
  return (
    <g>
      <path d={d} fill="none" stroke={color} strokeWidth={1.7} strokeDasharray={dashed ? '6 4' : undefined} opacity={dashed ? 0.6 : 1} markerEnd={`url(#arrow-${color.slice(1)})`} />
      {label && lx !== undefined && ly !== undefined && (
        <text x={lx} y={ly} fill={color} textAnchor={anchor ?? 'start'} className="font-mono text-[10px] font-semibold">{label}</text>
      )}
    </g>
  );
}

export function SysHealthMap({ snap, selected, onSelect }: { snap: SysHealth | null; selected: NodeId | null; onSelect: (id: NodeId) => void }) {
  const rsh = snap?.rsh ?? [];
  const publisher = snap?.feedgw.summary?.publisher ?? null;
  const fg = snap?.feedgw;
  const en = snap?.engine;
  const feed = en?.summary?.feed ?? null;
  const acct = en?.summary?.account ?? null;
  const instances = en?.summary?.instances ?? [];
  const armed = instances.filter((i) => i.armed === true).length;
  const liveN = instances.filter((i) => i.armed === true && i.mode === 'live').length;
  const kill = en?.summary?.kill ?? null;
  const hub = snap?.relay.accountHub as { mode?: string; connected?: boolean } | null;

  // Column of rsh boxes: one per floor source, in rank order, 110 px apart.
  const rshBoxes = rsh.map((b, i) => {
    const y = 50 + i * 112;
    const s = b.summary;
    const state: 'ok' | 'warn' | 'down' = !b.ok ? 'down' : s?.ready && s.lanesValid ? 'ok' : 'warn';
    const isPub = publisher !== null && b.id === publisher;
    return { b, y, state, isPub, lines: b.ok && s
      ? [`${fmt.int(s.markets)} markets · ${s.sports} sports`, `${s.consumers} consumer${s.consumers === 1 ? '' : 's'} · lanes ${s.lanesValid ? 'valid' : 'NOT valid'}`, isPub ? 'floor primary (feedgw)' : `standby ${b.rank + 1}`]
      : [b.error ?? 'unreachable', '', `rank ${b.rank + 1}`] };
  });
  const colH = Math.max(380, 50 + rsh.length * 112);
  const H = Math.max(820, colH + 440);

  return (
    <svg viewBox={`0 0 1240 ${H}`} role="img" aria-label="Live map of the Rust lane: Kalshi, the rsh boxes, wz0 and the clients" className="w-full min-w-[960px] font-mono">
      <defs>
        {Object.values(C).map((c) => (
          <marker key={c} id={`arrow-${c.slice(1)}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill={c} />
          </marker>
        ))}
      </defs>
      <text x="16" y="22" className="fill-foreground text-[12px] font-semibold">VENUE</text>
      <text x="232" y="22" className="fill-foreground text-[12px] font-semibold">RUST HYDRAS</text>
      <text x="528" y="22" className="fill-foreground text-[12px] font-semibold">wz0 · rst lane</text>
      <text x="984" y="22" className="fill-foreground text-[12px] font-semibold">CLIENTS</text>

      <Node id="kalshi" x={16} y={70} w={160} h={90} title="Kalshi market data" lines={['trade-api/ws/v2', 'orderbook_delta · ticker', 'trade · 4 pipes per key']} state="off" selected={selected === 'kalshi'} onSelect={onSelect} />
      <Node id="kalshi" x={16} y={200} w={160} h={60} title="Kalshi public REST" lines={['GET /markets · no key', 'discovery every 60 s']} state="off" selected={false} onSelect={onSelect} />
      <Node id="kalshi" x={16} y={colH + 140} w={160} h={96} title="Kalshi trading API" lines={['REST: place · cancel', 'decrease (rst key)', 'account WS: fills, positions']} state="off" selected={false} onSelect={onSelect} />

      {/* venue → rsh trunk */}
      <path d={`M176 115 H204 V${rshBoxes.length ? rshBoxes[rshBoxes.length - 1]!.y + 50 : 115}`} fill="none" stroke={C.data} strokeWidth={1.7} />
      <path d="M176 230 H204" fill="none" stroke={C.data} strokeWidth={1.7} />
      {rshBoxes.map(({ b, y }) => <Edge key={`in-${b.id}`} d={`M204 ${y + 50} H232`} color={C.data} />)}

      {rshBoxes.map(({ b, y, state, lines }) => (
        <Node key={b.id} id={`rsh:${b.id}`} x={232} y={y} w={240} h={96} title={`${b.id} · ${(() => { try { return new URL(b.url).hostname; } catch { return b.url; } })()}`} lines={lines} state={state} selected={selected === `rsh:${b.id}`} onSelect={onSelect} />
      ))}
      {rshBoxes.map(({ b, y, isPub }, i) => (
        <Edge key={`fg-${b.id}`} d={i === 0 ? `M472 ${y + 40} H544` : `M472 ${y + 40} H${500 + i * 10} V${120 + i * 18} H544`} color={C.data} dashed={!isPub} label={isPub ? 'floor primary' : undefined} lx={476} ly={y + 32} />
      ))}
      {/* strategy feed: the engine reads the box its config names (rsh5 today) */}
      {rshBoxes.length > 0 && (
        <Edge d={`M472 ${rshBoxes[rshBoxes.length - 1]!.y + 70} H520 V410 H560`} color={C.data} label="markets + flow → engine" lx={478} ly={rshBoxes[rshBoxes.length - 1]!.y + 90} />
      )}

      {/* wz0 */}
      <rect x={528} y={30} width={400} height={H - 80} rx={6} fill="none" className="stroke-muted-foreground" strokeDasharray="4 4" />
      <Node id="feedgw" x={544} y={70} w={368} h={96} title="feedgw-rst (TS)" state={!fg ? 'off' : !fg.ok ? 'down' : fg.summary?.floorError ? 'warn' : 'ok'} selected={selected === 'feedgw'} onSelect={onSelect}
        lines={fg?.summary ? [`floor publisher ${fg.summary.publisher ?? '—'} · ${fg.summary.floorEdges.filter((e) => e.live).length} live edge(s)`, `${fmt.int(fg.summary.tickers)} tickers on floor · ${fmt.int(fg.summary.duplicateRowsDropped)} fleet dups dropped`, `${fg.summary.clients} browser socket(s) · fallback ${fg.summary.fallbackLabels.length ? fg.summary.fallbackLabels.join(',') : 'none'}`] : [fg?.error ?? 'no snapshot', '', '']} />
      <Node id="relay" x={544} y={190} w={368} h={96} title="relay-rst (TS)" state={!snap ? 'off' : hub?.mode === 'hub' ? (hub.connected === false ? 'warn' : 'ok') : 'warn'} selected={selected === 'relay'} onSelect={onSelect}
        lines={['auth · UI REST · SSE /api/stream · games, odds', `account hub ${hub?.mode ?? '—'}${hub?.connected === false ? ' (disconnected)' : ''} · Penelope OFF`, 'Postgres g1mt_rst · users streamrift0-9']} />
      <Node id="engine" x={544} y={310} w={368} h={150} title="g1-engine-rst (Rust)" state={!en ? 'off' : !en.ok ? 'down' : kill ? 'warn' : feed?.connected === true ? 'ok' : 'warn'} selected={selected === 'engine'} onSelect={onSelect}
        lines={en?.summary ? [
          `feed ${feed?.connected === true ? 'connected' : 'DOWN'} · ${fmt.int(feed?.markets)} markets · ${fmt.int(feed?.flowGaps)} flow gaps`,
          `account ${String(acct?.gate ?? '—')} · cash ${fmt.usd(acct?.cashUsd)}`,
          `${instances.length} instance(s) · ${armed} armed · ${liveN} live`,
          kill ? `KILL LATCHED: ${JSON.stringify(kill).slice(0, 48)}` : 'kill: none',
          'gateway :9371 · account hub :9370 · admin :9373',
          'strategy host · hydra-client · Postgres g1e.*',
        ] : [en?.error ?? 'no snapshot', '', '', '', '', '']} />
      <text x={554} y={H - 64} className="fill-muted-foreground text-[10.5px]">rfq, peter, paine, army, node11 lanes share this box</text>

      {/* inside wz0 */}
      <Edge d="M824 286 V310" color={C.exec} label="order-bridge-v1" lx={830} ly={302} />
      <Edge d="M700 310 V286" color={C.acct} label="accthub-v1" lx={640} ly={302} anchor="end" />
      <Edge d="M488 180 H544" color={C.ctl} dashed label="TS fleet: odds, game state" lx={478} ly={176} anchor="end" />

      {/* engine ↔ Kalshi loops */}
      <Edge d={`M912 400 H960 V${H - 40} H120 V${colH + 236}`} color={C.exec} label="signed REST: place / cancel / decrease as streamriftN" lx={540} ly={H - 46} anchor="middle" />
      <Edge d={`M150 ${colH + 236} V${H - 26} H950 V430 H912`} color={C.acct} label="account WS: fills, orders, positions" lx={540} ly={H - 12} anchor="middle" />

      {/* clients */}
      <Node id="browser" x={984} y={70} w={240} h={96} title="Browser · rst.qeezi.com" state={!fg ? 'off' : fg.ok ? 'ok' : 'warn'} selected={selected === 'browser'} onSelect={onSelect}
        lines={['/feedws → feedgw (rows, books)', '/api, /api/stream → relay', `${fg?.summary?.clients ?? '—'} feed socket(s) open`]} />
      <Edge d="M912 118 H984" color={C.data} label="WS" lx={948} ly={110} anchor="middle" />
      <Edge d="M912 238 H950 V140 H984" color={C.ctl} dashed label="REST + SSE" lx={954} ly={232} />
      <Edge d="M984 150 H964 V260 H912" color={C.exec} label="manual orders" lx={968} ly={278} />
      <Node id="operator" x={984} y={310} w={240} h={70} title="Operator" state="off" selected={selected === 'operator'} onSelect={onSelect}
        lines={['StreamRift mode → /api/admin/rust', 'create · arm · mode · kill']} />
      <Edge d="M984 345 H912" color={C.ctl} dashed />
    </svg>
  );
}
